import { useNavigate } from "@tanstack/react-router";
import { FileSpreadsheet } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { CsvUploadPanel } from "@/components/import-work-orders/CsvUploadPanel";
import { ImportRowsTable } from "@/components/import-work-orders/ImportRowsTable";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import type { ImportRow } from "@/mocks/import-work-orders";
import { queueMassUpdateJob } from "@/mocks/mass-update-jobs";

export function ImportWorkOrdersWorkspace() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);

  const validCount = rows.filter((row) => row.isValid).length;
  const invalidCount = rows.length - validCount;

  function handleParsed(parsedRows: ImportRow[], name: string) {
    setRows(parsedRows);
    setFileName(name);
    if (parsedRows.length === 0) {
      toast.error("No rows found in that file.");
    }
  }

  function handleClear() {
    setRows([]);
    setFileName(null);
  }

  function handleApplyConfirmed() {
    queueMassUpdateJob("Work Order Import", validCount);
    toast.success(`Queued an import for ${validCount} work order(s).`);
    setApplyOpen(false);
    navigate({ to: "/dashboard/jobs" });
  }

  return (
    <div className="space-y-6">
      <CsvUploadPanel onParsed={handleParsed} />

      {rows.length === 0 ? (
        <EmptyState
          icon={FileSpreadsheet}
          title="No file uploaded yet"
          description="Choose a CSV file, or download the sample above to try the flow."
        />
      ) : (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {fileName && <span className="font-medium text-foreground">{fileName}</span>}{" "}
              {validCount} valid row{validCount === 1 ? "" : "s"}
              {invalidCount > 0 && ` · ${invalidCount} invalid — those will be skipped`}.
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={handleClear}>
                Clear
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => setApplyOpen(true)}
                disabled={validCount === 0}
              >
                Apply import
              </Button>
            </div>
          </div>
          <ImportRowsTable rows={rows} />
        </>
      )}

      <ConfirmDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        title={`Create ${validCount} work order${validCount === 1 ? "" : "s"}?`}
        description={
          invalidCount > 0
            ? `This will create ${validCount} work order(s) in NetSuite and skip ${invalidCount} invalid row(s). This queues a job — nothing in NetSuite is touched from this draft.`
            : `This will create ${validCount} work order(s) in NetSuite. This queues a job — nothing in NetSuite is touched from this draft.`
        }
        confirmLabel="Apply import"
        onConfirm={handleApplyConfirmed}
      />
    </div>
  );
}
